import { SUIT_NAMES, SUIT_SYMBOLS, isRed, type Card } from '@cardengine/shared';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useStore } from '../store.js';
import { CardBack, PlayingCard } from './PlayingCard.js';

export const DISCARD_DROP_ID = 'discard-pile';
export const DRAW_DRAG_ID = 'draw-pile';

/**
 * A stable pseudo-random offset per card, so the pile looks hand-dealt but
 * never jitters between renders.
 */
function scatter(seed: string, index: number) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const a = ((h >>> 0) % 1000) / 1000;
  const b = ((h >>> 9) % 1000) / 1000;
  return {
    rotate: (a - 0.5) * 17,
    x: (b - 0.5) * 13,
    y: (a - 0.5) * 9 - index * 0.5,
  };
}

export function DiscardPile() {
  const game = useStore((s) => s.game);
  const selectedCardIds = useStore((s) => s.selectedCardIds);
  const { setNodeRef, isOver } = useDroppable({ id: DISCARD_DROP_ID });

  if (!game) return null;

  const armed = selectedCardIds.length > 0;
  const suitDiffers = game.activeSuit !== game.topCard.suit;
  // Oldest first so the top card renders last and sits on top.
  const pile: Card[] = game.recentDiscards.length > 0 ? game.recentDiscards : [game.topCard];

  return (
    <div className="pile pile--discard">
      <div
        ref={setNodeRef}
        className={`pile__drop ${isOver ? 'pile__drop--over' : ''} ${armed ? 'pile__drop--armed' : ''}`}
      >
        <div className="pile__scatter">
          {pile.map((card, index) => {
            const isTop = index === pile.length - 1;
            const { rotate, x, y } = isTop ? { rotate: 0, x: 0, y: 0 } : scatter(card.id, index);
            return (
              <div
                key={card.id}
                className={`pile__scatter-card ${isTop ? 'is-top' : ''}`}
                style={{ transform: `translate(${x}px, ${y}px) rotate(${rotate}deg)`, zIndex: index }}
              >
                <PlayingCard card={card} />
              </div>
            );
          })}
        </div>

        {suitDiffers && (
          <span
            className={`pile__suit-badge ${isRed(game.activeSuit) ? 'is-red' : 'is-black'}`}
            title={`Suit changed to ${SUIT_NAMES[game.activeSuit]}`}
          >
            {SUIT_SYMBOLS[game.activeSuit]}
          </span>
        )}
      </div>
      <span className="pile__label">{suitDiffers ? `Now ${SUIT_NAMES[game.activeSuit]}` : 'Discard'}</span>
    </div>
  );
}

export function DrawPile() {
  const game = useStore((s) => s.game);

  const drawMove = game?.yourLegalMoves.find((m) => m.kind === 'draw');
  const canDraw = Boolean(drawMove);
  const penalty = drawMove?.kind === 'draw' && drawMove.reason === 'penalty' ? drawMove.count : 0;

  // With bluffing on, drawing is legal every turn — so the pile must only
  // clamour for attention when it is genuinely the one thing left to do.
  const hasPlay = game?.yourLegalMoves.some((m) => m.kind === 'play') ?? false;
  const mustDraw = canDraw && !hasPlay;

  // Taking a card is a deliberate action: you drag it into your hand.
  // Note: no transform is applied to this element. A DragOverlay renders the
  // card that follows the cursor, so moving the source too would carry the
  // whole deck away and leave an empty space where the pile should be.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: DRAW_DRAG_ID,
    disabled: !canDraw,
  });

  if (!game) return null;

  const label =
    penalty > 0
      ? `Drag ${penalty} into your hand`
      : mustDraw
        ? 'Drag into your hand'
        : `Draw · ${game.drawPileCount}`;

  return (
    <div className="pile pile--draw">
      <div
        ref={setNodeRef}
        className={`pile__grab ${canDraw ? 'pile__grab--active' : ''} ${
          mustDraw ? 'pile__grab--urgent' : ''
        } ${isDragging ? 'pile__grab--dragging' : ''}`}
        {...listeners}
        {...attributes}
        role="button"
        tabIndex={canDraw ? 0 : -1}
        aria-disabled={!canDraw}
        aria-label={penalty > 0 ? `Pick up ${penalty} cards` : 'Draw a card'}
      >
        <span className="pile__stack">
          <CardBack className="pile__stack-card pile__stack-card--4" />
          <CardBack className="pile__stack-card pile__stack-card--3" />
          <CardBack className="pile__stack-card pile__stack-card--2" />
          <CardBack className="pile__stack-card pile__stack-card--1" />
        </span>
        {penalty > 0 && <span className="pile__penalty">+{penalty}</span>}
      </div>
      <span className="pile__label">{label}</span>
    </div>
  );
}
